'use client';

import { User } from '@supabase/supabase-js';
import { ROLES, RoleType } from 'types/roles';

import { createClient } from '@utils/supabase/client';

export interface UserData {
  role: RoleType | null;
  currentPath: string | null;
}

export interface UploadResult {
  success: boolean;
  newPath?: string;
  error?: string;
}

const MAX_SOURCE_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_AVATAR_DIMENSION = 800;
const JPEG_QUALITY = 0.82;
const JPEG_EXTENSION = 'jpg';
const JPEG_MIME_TYPE = 'image/jpeg';

/**
 * Get user role and current image path
 */
export async function getUserImageData(user: User): Promise<UserData> {
  try {
    if (!user) {
      return { role: null, currentPath: null };
    }

    const supabase = createClient();

    // Check user metadata for role first
    const userRole = user.user_metadata?.role;

    if (userRole === ROLES.SCHOOL) {
      // Only check school table
      const { data: schoolData, error: schoolError } = await supabase
        .from('user_school')
        .select('photo_path')
        .eq('id', user.id)
        .single();

      if (schoolData && !schoolError) {
        return { role: ROLES.SCHOOL, currentPath: schoolData.photo_path };
      }
    } else if (userRole === ROLES.TEACHER) {
      // Only check teacher table
      const { data: teacherData, error: teacherError } = await supabase
        .from('user_teacher')
        .select('avatar_url')
        .eq('id', user.id)
        .single();

      if (teacherData && !teacherError) {
        return { role: ROLES.TEACHER, currentPath: teacherData.avatar_url };
      }
    } else {
      // Fallback: check both tables if role is unclear
      const { data: schoolData } = await supabase
        .from('user_school')
        .select('photo_path')
        .eq('id', user.id)
        .single();

      if (schoolData) {
        return { role: ROLES.SCHOOL, currentPath: schoolData.photo_path };
      } else {
        const { data: teacherData } = await supabase
          .from('user_teacher')
          .select('avatar_url')
          .eq('id', user.id)
          .single();

        if (teacherData) {
          return { role: ROLES.TEACHER, currentPath: teacherData.avatar_url };
        }
      }
    }

    return { role: null, currentPath: null };
  } catch (err) {
    throw new Error(
      `Failed to load user profile: ${err instanceof Error ? err.message : 'Unknown error'}`,
    );
  }
}

/**
 * Create signed URL for private image
 */
export async function createSignedImageUrl(path: string): Promise<string | null> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase.storage.from('avatars').createSignedUrl(path, 3600);

    if (error) {
      throw new Error(`Failed to generate image URL: ${error.message}`);
    }

    return data.signedUrl;
  } catch (error) {
    throw new Error(
      `Failed to generate image URL: ${error instanceof Error ? error.message : 'Unknown error'}`,
    );
  }
}

/**
 * Validate image file
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select an image file (JPG, PNG, etc.)' };
  }

  if (file.size > MAX_SOURCE_IMAGE_SIZE) {
    return { valid: false, error: 'File size must be less than 10MB' };
  }

  return { valid: true };
}

/**
 * Resize large profile photos before uploading. Avatars never need phone-camera dimensions.
 */
async function prepareImageForUpload(file: File): Promise<File> {
  const image = await loadImage(file);
  const { width, height } = getResizedDimensions(image.width, image.height);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Could not prepare image for upload');
  }

  // Avatars are always stored as JPEG, which has no alpha channel. Paint a white
  // background first so transparent areas of PNGs become white instead of black.
  context.fillStyle = '#fff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);

  const blob = await canvasToBlob(canvas);

  // Release the pixel buffer immediately instead of waiting for GC.
  canvas.width = 0;
  canvas.height = 0;

  return new File([blob], getJpegFileName(file.name), {
    type: JPEG_MIME_TYPE,
    lastModified: Date.now(),
  });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read image file'));
    };

    image.src = objectUrl;
  });
}

function getResizedDimensions(width: number, height: number): { width: number; height: number } {
  const longestSide = Math.max(width, height);

  if (longestSide <= MAX_AVATAR_DIMENSION) {
    return { width, height };
  }

  const scale = MAX_AVATAR_DIMENSION / longestSide;

  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Could not prepare image for upload'));
        }
      },
      JPEG_MIME_TYPE,
      JPEG_QUALITY,
    );
  });
}

function getJpegFileName(fileName: string): string {
  const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
  return `${nameWithoutExtension || 'avatar'}.${JPEG_EXTENSION}`;
}

/**
 * Upload image and update database
 */
export async function uploadUserImage(
  file: File,
  user: User,
  userRole: RoleType,
  currentPath: string | null,
): Promise<UploadResult> {
  if (!userRole) {
    return { success: false, error: 'User profile not found. Please refresh and try again.' };
  }

  try {
    const supabase = createClient();
    const uploadFile = await prepareImageForUpload(file);

    // Build upload path based on user role
    const ext = uploadFile.name.split('.').pop() || JPEG_EXTENSION;
    const fileName = `${userRole}_${user.id}_${Date.now()}.${ext}`;
    const newPath = `${userRole === ROLES.SCHOOL ? 'schools' : 'teachers'}/${fileName}`;

    // Upload new image
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(newPath, uploadFile, { cacheControl: '3600', upsert: true });

    if (uploadError) {
      return {
        success: false,
        error: `Upload failed: ${uploadError.message}. Please try again.`,
      };
    }

    // Update database based on user role
    const tableName = userRole === ROLES.SCHOOL ? 'user_school' : 'user_teacher';
    const columnName = userRole === ROLES.SCHOOL ? 'photo_path' : 'avatar_url';

    const { error: updateError } = await supabase
      .from(tableName)
      .update({ [columnName]: uploadData.path })
      .eq('id', user.id);

    if (updateError) {
      return {
        success: false,
        error: `Failed to update profile: ${updateError.message}`,
      };
    }

    // Delete old image in background (don't wait for this)
    if (currentPath) {
      supabase.storage
        .from('avatars')
        .remove([currentPath])
        .catch(() => {
          // Silent failure for cleanup - not critical
        });
    }

    return { success: true, newPath: uploadData.path };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.',
    };
  }
}
